import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Car, MapPin, Sparkle } from "@phosphor-icons/react";
import { fmtKm } from "@/lib/format";
import TripFormDialog from "@/components/forms/TripFormDialog";

/**
 * LogTripButton — the smart "Log a trip" entry point.
 *
 * Behaviour:
 *  - If the user has an existing trip TODAY, opens a small chooser so the
 *    new receipt/expense can be attached to the SAME trip (preserving the
 *    drive mileage) instead of creating a duplicate trip with 0 km.
 *  - If there are no trips today, opens the standard new-trip dialog
 *    directly (no chooser overhead).
 *
 * This was added after a user uploaded a meal receipt and accidentally
 * created a second trip on the same day with 0 km, then asked "it seemed
 * to take off the mileage" — Scenario A from the bug triage. Defaulting
 * to "add to today's trip" prevents the double-trip confusion.
 */
export default function LogTripButton({ trips, todayISO, trigger }) {
  const [chooserOpen, setChooserOpen] = useState(false);
  const [editTripId, setEditTripId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  // Only TRIP entries (not Rx-only entries) get the same-day match,
  // because Rx-only entries don't have mileage you'd want to share.
  const todaysTrips = useMemo(
    () =>
      (trips || []).filter(
        (t) =>
          t.date === todayISO &&
          (t.entry_type || "trip") === "trip"
      ),
    [trips, todayISO]
  );

  const editingTrip = useMemo(
    () => todaysTrips.find((t) => t.id === editTripId) || null,
    [todaysTrips, editTripId]
  );

  const handleMainClick = () => {
    if (todaysTrips.length > 0) {
      setChooserOpen(true);
    } else {
      setNewOpen(true);
    }
  };

  const handlePickExisting = (t) => {
    setEditTripId(t.id);
    setChooserOpen(false);
  };

  const handlePickNew = () => {
    setChooserOpen(false);
    setNewOpen(true);
  };

  return (
    <>
      {trigger ? (
        // Custom trigger (e.g. the new circle quick-action button on the
        // dashboard). We clone the caller's element and attach our smart
        // handler so their onClick receives the same-day-chooser logic.
        <span
          onClick={handleMainClick}
          data-testid="log-trip-smart-trigger-wrap"
          className="inline-flex"
        >
          {trigger}
        </span>
      ) : (
        <Button
          data-testid="log-trip-smart-trigger"
          onClick={handleMainClick}
          className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
        >
          <Plus size={18} className="mr-2" /> Log a trip
        </Button>
      )}

      <Dialog open={chooserOpen} onOpenChange={setChooserOpen}>
        <DialogContent
          className="sm:max-w-md rounded-2xl"
          data-testid="log-trip-chooser"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={20} weight="duotone" className="text-[#2C4C3B]" />
              Add to today&apos;s trip?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C6660]">
            You already have {todaysTrips.length === 1 ? "a trip" : `${todaysTrips.length} trips`} logged today. Add this receipt/expense to the same trip so your mileage stays linked — CRA won&apos;t let you claim mileage twice for the same drive.
          </p>
          <div className="space-y-2 mt-1" data-testid="log-trip-chooser-options">
            {todaysTrips.map((t) => (
              <button
                key={t.id}
                onClick={() => handlePickExisting(t)}
                data-testid={`chooser-existing-${t.id}`}
                className="w-full text-left rounded-xl border border-[#E3E1D9] hover:border-[#2C4C3B] hover:bg-[#2C4C3B]/5 p-3 transition-colors"
              >
                <div className="font-medium text-sm text-[#1E2420] truncate">
                  {t.destination || t.purpose || "Untitled trip"}
                </div>
                <div className="text-xs text-[#5C6660] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Car size={12} weight="duotone" /> {fmtKm(t.total_km || 0)}
                  </span>
                  {t.purpose && t.destination && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={12} weight="duotone" /> {t.purpose}
                    </span>
                  )}
                  {t.provider && (
                    <span className="truncate">· {t.provider}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={handlePickNew}
            data-testid="chooser-new-trip"
            className="w-full rounded-full border-[#E3E1D9] mt-1"
          >
            <Plus size={14} className="mr-1.5" /> Start a separate new trip
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit existing — controlled open. trigger=null hides the default
          button since we're opening programmatically. The `key` prop is
          critical: it forces TripFormDialog to remount each time
          editTripId changes, which re-runs the useState(initializer) so
          the form is correctly preloaded with the selected trip's data.
          Without the key, useState fires once at mount with initial=undefined
          and the form stays blank forever. */}
      <TripFormDialog
        key={editTripId ? `edit-${editTripId}` : "edit-none"}
        initial={editingTrip || undefined}
        open={!!editingTrip}
        onOpenChange={(v) => {
          if (!v) setEditTripId(null);
        }}
        trigger={null}
      />

      {/* New trip — controlled open. trigger=null again. Remount on each
          new-trip open so the form is fresh (no stale state from a
          previously-cancelled new trip). */}
      <TripFormDialog
        key={`new-${newOpen ? "open" : "closed"}`}
        open={newOpen}
        onOpenChange={setNewOpen}
        trigger={null}
      />
    </>
  );
}
